import React, { useState, useRef, useEffect } from 'react';
import MaterialTable from 'material-table';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import { fetchAllTruckMaintenanceData, fetchTruckNumbers } from '../common/TruckMaintenanceUtilities';


const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Example data for the two header rows
// const trucks = [201,203,204,205,206,207,208,209,210]
const headerLabels = [
  'wash', 'grease', 'oil change'
];

const truckColors = ['rgb(0,176,240)', 'rgb(255,204,153)', 'rgb(204,255,204)']

export default function MaintenaceOverview(props) {

  const onClose = props.onClose
  const [open] = useState(props.open)
  const columns = useRef([]);
  const [maintenanceData, setMaintenanceData] = useState(null);

  useEffect(async () => {
    
    console.log('Fetching truck numbers...');

    (async () => {
        // Fetch truck numbers from the database
        let trucks = await fetchTruckNumbers(props.db)
        trucks = trucks.slice().sort((a, b) => Number(a) - Number(b));
        const validTrucks = [201, 203, 204, 205, 206, 207, 208, 209];
        trucks = trucks.filter(truck => validTrucks.includes(Number(truck)));
        console.log('Truck numbers fetched:', trucks);

        // Build columns: first column is fixed for months, rest are for categories
        columns.current = [
        {
            title: '',
            field: 'month',
            editable: 'never',
            cellStyle: { fontWeight: 'bold', minWidth: 120, background: '#f5f5f5', color: 'red' },
            headerStyle: { fontWeight: 'bold', minWidth: 120, background: '#f5f5f5' },
            width: 120,
            fixed: 'left'
        },
        ...trucks.flatMap((truck, truckIdx) =>
            headerLabels.map((label, labelIdx) => ({
                // Use truck and label to create a unique column title
                // The title is a div with two lines: truck number and label
                // The truck number is centered and has a background color based on its index
                title: (
                <div style={{ textAlign: 'center', leftPadding: 0, rightPadding: 0  }}>
                    <div style={{ fontSize: 18, fontWeight: 'bold', background: truckColors[truckIdx % truckColors.length], border: '1px solid black' }}>{truck}</div>
                    <div style={{ fontSize: 12, border: '1px solid black' }}>{label}</div>
                </div>
                ),
                field: `col_${truck}_${label}`,
                align: 'center',
                width: 100,
                cellStyle: (value, rowData) => {
                    const baseStyle = { textAlign: 'left', fontSize: 12, border: '1px solid black' };
                    
                    // Check if this is the current month row (now row index 2, third row)
                    if (rowData.tableData && rowData.tableData.id === 2) {
                        const truckId = String(truck);
                        const currentMileage = currentMileages[truckId] || 0;
                        
                        if (label === 'wash') {
                            // If no wash value in current month, highlight with yellow background
                            if (!value || value === '') {
                                return { ...baseStyle, backgroundColor: 'yellow' };
                            }
                        } else if (label === 'grease') {
                            // If no greasing value in current month, highlight with yellow background
                            if (!value || value === '') {
                                return { ...baseStyle, backgroundColor: 'yellow' };
                            }
                        } else if (label === 'oil change') {
                            // Find the latest oil change mileage for this truck
                            const oilChanges = maintenancedata[truckId]?.['oil change'] || [];
                            if (oilChanges.length === 0) {
                                // No previous oil change data, highlight yellow
                                return { ...baseStyle, backgroundColor: 'yellow' };
                            } else {
                                // Find the most recent oil change
                                const latestOilChange = oilChanges.reduce((latest, current) => 
                                    current.date > latest.date ? current : latest
                                );
                                const mileageDifference = currentMileage - latestOilChange.mileage;
                                // If it's been 23,000 miles or more since last oil change, highlight yellow
                                if (mileageDifference >= 23000) {
                                    return { ...baseStyle, backgroundColor: 'yellow' };
                                }
                            }
                        }
                    }
                    
                    return baseStyle;
                },
            }))
        )
        ]

        // Build data: blank row, then 24 months starting from current month going backwards
        const currentDate = new Date();
        const monthsData = [];
        
        // Generate 24 months going backwards from current month
        for (let i = 0; i < 24; i++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
            const monthName = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear();
            monthsData.push({ month: `${monthName} ${year}`, monthIndex: date.getMonth(), year: year });
        }
        
        const data = [
            { month: '' }, // blank row
            { month: 'Mileage' }, // current mileage row
            ...monthsData
        ];

        // Fetch maintenance data when component mounts
        const maintenancedata = await fetchAllTruckMaintenanceData(props.db)
        
        // Fetch truck data to get current mileage from latest inspections
        const trucksSnap = await props.db.collection('trucks').get();
        const trucksData = {};
        trucksSnap.docs.forEach(doc => {
            const data = doc.data();
            if (trucks.includes(data.truck)) {
                trucksData[String(data.truck)] = data;
            }
        });

        // Fetch current mileage from latest truck inspections
        const currentMileages = {};
        for (const truck of trucks) {
            const truckId = String(truck);
            const truckData = trucksData[truckId];
            
            if (truckData && truckData.lastInspectionId) {
                try {
                    const inspectionDoc = await props.db.collection('truck_inspections').doc(truckData.lastInspectionId).get();
                    if (inspectionDoc.exists) {
                        const inspectionData = inspectionDoc.data();
                        currentMileages[truckId] = inspectionData.mileage || 0;
                    } else {
                        currentMileages[truckId] = 0;
                    }
                } catch (error) {
                    console.warn(`Error fetching inspection for truck ${truck}:`, error);
                    currentMileages[truckId] = 0;
                }
            } else {
                currentMileages[truckId] = 0;
            }
        }
        
        // Populate current mileage row with mileage data for each truck
        trucks.forEach(truck => {
            const truckId = String(truck);
            const currentMileage = currentMileages[truckId] || 0;
            // Show current mileage in all columns for this truck
            headerLabels.forEach(label => {
                data[1][`col_${truck}_${label}`] = currentMileage.toLocaleString();
            });
        });
        
        // Populate data array with maintenance data for the 24-month period
        monthsData.forEach((monthData, dataIndex) => {
            const { monthIndex, year } = monthData;
            const monthName = months[monthIndex];
            
            trucks.forEach(truck => {
                const truckId = String(truck);
                const washes = maintenancedata[truckId]?.washes || [];
                const greasings = maintenancedata[truckId]?.greasings || [];
                const oilChanges = maintenancedata[truckId]?.['oil change'] || [];

                // Washes: latest date in the month for the specific year
                if (washes.length !== 0) {
                    let value = '';
                    let values = washes.filter(date => 
                        date.toLocaleString('default', { month: 'short' }) === monthName &&
                        date.getFullYear() === year
                    );
                    if (values.length > 0) {
                        let latest = values.reduce((a, b) => (a > b ? a : b));
                        value = latest.toLocaleDateString('default');
                    }
                    data[dataIndex + 2][`col_${truck}_wash`] = value;
                }

                // Greasings: latest date and mileage in the month for the specific year
                if (greasings.length !== 0) {
                    let value = '';
                    let values = greasings.filter(greasing => 
                        greasing.date && greasing.date.toLocaleString('default', { month: 'short' }) === monthName &&
                        greasing.date.getFullYear() === year
                    );
                    if (values.length > 0) {
                        let latest = values.reduce((a, b) => (a.date > b.date ? a : b));
                        const dateStr = latest.date.toLocaleDateString('default');
                        const mileage = latest.mileage || 0;
                        value = `${dateStr}: ${mileage.toLocaleString()}`;
                    }
                    data[dataIndex + 2][`col_${truck}_grease`] = value;
                }

                // Oil changes: latest date and mileage in the month for the specific year
                if (oilChanges.length !== 0) {
                    let value = '';
                    let oilChangeObjs = oilChanges.filter(
                        obj => obj.date && 
                        obj.date.toLocaleString('default', { month: 'short' }) === monthName &&
                        obj.date.getFullYear() === year
                    );
                    if (oilChangeObjs.length > 0) {
                        let latest = oilChangeObjs.reduce((a, b) => (a.date > b.date ? a : b));
                        const dateStr = latest.date.toLocaleDateString('default');
                        const mileage = latest.mileage || 0;
                        value = `${dateStr}: ${mileage.toLocaleString()}`;
                    }
                    data[dataIndex + 2][`col_${truck}_oil change`] = value;
                }
            });
        });
        setMaintenanceData(data)
        })()
  }, [props.db ]);

  const close = () => {
    onClose()
  }

  const onDoClose = () => {
    close()
  } 

  if (!maintenanceData) return (<div>Loading...</div>)  

  return (
    <Dialog open={open} onClose={close} fullScreen>
        <DialogTitle id="simple-dialog-title">Truck Maintenance Overview</DialogTitle>
        <DialogContent>
            <MaterialTable
                title="Maintenance Overview"
                columns={columns.current}
                data={maintenanceData}
                options={{
                    paging: false,
                    search: false,
                    sorting: false,
                    headerStyle: { whiteSpace: 'nowrap', padding: '1px', width: '100px' },
                    fixedColumns: { left: 1 },
                    padding: 'dense'
                }}
            />
        </DialogContent>
        <DialogActions>
            <Button color="primary" onClick={onDoClose}>Close</Button>
        </DialogActions>
    </Dialog>
    
  );
}